import axios from 'axios';

// NEW OTP Handler - Using local OTP generation and L&T Finance SMS API
const OTP_HANDLER_URL = "/otp-handler.php";
const COMPANY_DATA_URL = "/proxy.php?endpoint=DigitalCoreService_posterSrvc";

/**
 * Get session ID from sessionStorage
 */
const getSessionId = () => {
  return sessionStorage.getItem('ltf_session_id') || '';
};

/**
 * Sends an OTP to the provided phone number
 * NEW: Generates OTP locally, stores in Supabase, sends via L&T Finance SMS API
 * @param {string} phoneNumber - Phone number without country code (10 digits)
 * @param {string} customerName - Name of the customer (optional, not used in current flow)
 * @returns {Promise<{success: boolean, otp?: string, message?: string}>} 
 */
export const sendOtp = async (phoneNumber, customerName = "Customer") => {
  try {
    console.log('📱 Sending OTP to:', phoneNumber);
    
    // Validate phone number format
    const cleanPhone = phoneNumber.toString().replace(/\D/g, '').slice(-10);
    if (cleanPhone.length !== 10) {
      return {
        success: false,
        message: "Invalid phone number format. Please enter 10 digits."
      };
    }
    
    // Prepare request payload
    const payload = {
      action: 'send',
      phoneNumber: cleanPhone,
      sessionId: getSessionId()
    };
    
    // Make API call to our OTP handler
    console.log('🔵 Sending OTP Request:', payload);
    
    const response = await axios.post(OTP_HANDLER_URL, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    console.log('✅ OTP Response:', response.data);
    
    // Show debug info if available
    if (response.data.debug) {
      console.log('🔍 Debug Info:', response.data.debug);
    }
    
    if (response.data.success) {
      return {
        success: true,
        otp: response.data.otp || null, // OTP returned in UAT mode for testing
        message: response.data.message || "OTP sent successfully",
        expiresIn: response.data.expiresIn || 180
      };
    } else {
      throw new Error(response.data.message || 'Failed to send OTP');
    }
    
  } catch (error) {
    console.error('❌ Send OTP Error:', error);
    
    // Handle specific error codes
    if (error.response?.data?.errorCode === 'RATE_LIMIT_EXCEEDED') {
      return {
        success: false,
        errorCode: 'RATE_LIMIT_EXCEEDED',
        message: error.response.data.message || "Too many OTP requests. Please wait before trying again."
      };
    }
    
    // Generic error response
    return {
      success: false,
      error: error.message,
      message: error.response?.data?.message || "Failed to send OTP. Please try again."
    };
  }
};

/**
 * Verifies an OTP for a given phone number
 * NEW: Verifies against Supabase database instead of external API
 * @param {string} phoneNumber - Phone number without country code
 * @param {string} otp - OTP code to verify
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const verifyOtp = async (phoneNumber, otp) => {
  try {
    console.log('🔐 Verifying OTP for:', phoneNumber);
    
    // Validate inputs
    const cleanPhone = phoneNumber.toString().replace(/\D/g, '').slice(-10);
    const cleanOtp = otp.toString().replace(/\D/g, '');
    
    if (cleanPhone.length !== 10) {
      return {
        success: false,
        message: "Invalid phone number format."
      };
    }
    
    if (cleanOtp.length !== 6) {
      return {
        success: false,
        message: "Invalid OTP format. Please enter 6 digits."
      };
    }
    
    // Prepare request payload
    const payload = {
      action: 'verify',
      phoneNumber: cleanPhone,
      otp: cleanOtp
    };
    
    // Make API call to our OTP handler
    const response = await axios.post(OTP_HANDLER_URL, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    console.log('✅ Verify Response:', response.data);
    
    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || "OTP verified successfully",
        sessionId: response.data.sessionId
      };
    } else {
      return {
        success: false,
        message: response.data.message || "Invalid OTP. Please try again.",
        errorCode: response.data.errorCode,
        remainingAttempts: response.data.remainingAttempts
      };
    }
    
  } catch (error) {
    console.error('❌ Verify OTP Error:', error);
    
    // Handle specific error codes
    const errorCode = error.response?.data?.errorCode;
    
    if (errorCode === 'SESSION_INVALID') {
      return {
        success: false,
        errorCode: 'SESSION_INVALID',
        message: error.response.data.message || "OTP session expired. Please request a new OTP."
      };
    }
    
    if (errorCode === 'OTP_EXPIRED') {
      return {
        success: false,
        errorCode: 'OTP_EXPIRED',
        message: error.response.data.message || "OTP has expired. Please request a new one."
      };
    }
    
    if (errorCode === 'MAX_ATTEMPTS_EXCEEDED') {
      return {
        success: false,
        errorCode: 'MAX_ATTEMPTS_EXCEEDED',
        message: error.response.data.message || "Too many failed attempts. Please request a new OTP."
      };
    }
    
    // Generic error response
    return {
      success: false,
      message: error.response?.data?.message || "Verification failed. Please try again."
    };
  }
};

/**
 * Fetches company data associated with a phone number
 * @param {string} phoneNumber - Phone number without country code
 * @returns {Promise<{success: boolean, companies?: Array<{agreementNo: string, companyName: string}>, message?: string}>}
 */
export const fetchCompanyData = async (phoneNumber) => {
  try {
    // Headers are now handled by proxy.php for security
    const headers = {
      "Content-Type": "application/json"
    };

    // Create request body - ensure number is formatted correctly (just the 10 digits without country code)
    const mobileNo = phoneNumber.toString().trim().replace(/\D/g, '').slice(-10);
    
    const requestBody = {
      "mobileNo": mobileNo
    };
    
    // Make the API call through our proxy to prevent CORS errors
    const response = await axios.post(COMPANY_DATA_URL, requestBody, { headers });
    
    if (response.status === 200 && response.data) {
      // Expect response data directly, it's already parsed by axios
      const data = response.data;
      
      // Check if the response contains company data
      if (data.statusCode === "200" && data.businessCampaignbean && data.businessCampaignbean.length > 0) {
        return {
          success: true,
          companies: data.businessCampaignbean,
          message: "Company data retrieved successfully"
        };
      } else {
        return {
          success: true,
          companies: [],
          message: "No company data found for this number"
        };
      }
    } else {
      return {
        success: false,
        companies: [],
        message: "Failed to retrieve company data"
      };
    }
  } catch (error) {
    return {
      success: false,
      companies: [],
      message: "Error fetching company data: " + (error.message || "Unknown error")
    };
  }
};